import * as yup from "yup";
import Reference, { ReferenceOptions } from "yup/lib/Reference";
import { AnyObject } from "yup/lib/types";
import { OfficeResponse } from "../../modules/api-docs";
import { timezoneIANAs } from "../types/TimezoneIANAEnum";
import { isEmptyRTF } from "../utils/trim-rtf";
import { isEmailInvalid } from "../utils/validation/validateEmail";
import { isValidLinkedInUrl } from "../utils/validation/validateLinkedInURL";

// Type safety for when the state is passed into the 'yup' validation as context so that fields can refer to cousin fields (other children of the field's parent), since 'ref' only lets one refer to a field's siblings and their children
export function yupSchemaContext<StateObject>(
  yupThis: yup.TestContext<AnyObject>,
): StateObject {
  return yupThis.options.context as StateObject;
}

// Doesn't work for strings, but does work for Dates (see stories). Hmm...
export function typeSafeYupRef<T>(
  key: keyof T & string,
  options?: ReferenceOptions<T>,
): Reference<T> {
  return yup.ref<T>(key, options);
}

/**
 * @deprecated
 *
 * @param possibleValues
 * @returns a `yup` enum type on the values of the passed-in array.
 */
export function yupEnum<T extends string | number | boolean>(
  possibleValues: readonly T[],
  message?: string,
) {
  return yup.mixed<T>().oneOf(possibleValues as T[], message);
}

export function yupLinkedIn(message?: string) {
  return yup.string().when({
    is: (v: string) => {
      return v !== "" && v !== null && v !== undefined;
    },
    then: yup
      .string()
      .test(
        "validateLinkedInURL",
        message ?? "Invalid LinkedIn URL",
        isValidLinkedInUrl,
      ),
  });
}

/**
 * A required `string` field which trims the string before validating.
 */
export const yupRequiredString = (
  errorMessage: string = "This field is required",
) => yup.string().trim().required(errorMessage);

export const yupEmail = (errorMessage: string = "Invalid email") =>
  yup
    .string()
    .trim()
    .test("validEmail", errorMessage, function (value) {
      return !isEmailInvalid(value ?? "");
    });

/**
 * Returns a `yup` object for a required rich text field.
 */
export const yupRequiredRTF = (
  errorMessage: string = "This field is required",
) =>
  yup
    .string()
    .test("Not empty string or empty <p>", errorMessage, function (values) {
      if (!values) return false;

      if (isEmptyRTF(values)) return false;

      return true;
    });

export const yupTZ = () => yupEnum(timezoneIANAs);

export const nullableYupTZ = () => yupTZ().nullable();

export const yupOffice: () => yup.SchemaOf<Required<OfficeResponse>> = () =>
  yup.object({
    id: yup.number(),
    name: yup.string(),
    description: yup.string(),
  });

/**
 * `jest` helper to test whether the `yup` schema throws a `ValidationError`.
 */
export function testYupValidationError<Obj, Key extends keyof Obj & string>(
  schema: yup.SchemaOf<Obj>,
  field: Key,
  value: Obj,
) {
  return () => schema.validateSyncAt(field, value);
}
